import config from "config";
import { Offer, OfferPublished } from "graphql/__generated__/globalTypes";
import useNotification from "hooks/useNotification";
import { useUnreadTransactionThreads } from "hooks/usePublishedOffer";
import { useRealtimeConnection } from "hooks/useRealTimeConnection";
import OfferThreadContainer from "pages/AllBusiness/components/OfferThreadContainer";
import { useEffect, useMemo, useState } from "react";

interface Notification {
  id: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
  routePath: string;
  offerPublished?: any;
  offer?: any;
  participationId: string;
  policyNumber?: string;
}

const NotificationBell = () => {
  const { notify } = useNotification();

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showThreadContainer, setShowThreadContainer] = useState(false);
  const [activeThread, setActiveThread] = useState<{
    offerPublished: OfferPublished;
    offer: Offer;
    participationId: string;
  } | null>(null);

  const { unreadThreads, loading, refresh } = useUnreadTransactionThreads();

  const unreadMessageIds = useMemo(
    () =>
      (unreadThreads
        ?.filter((msg) => msg !== null)
        .map((msg) => msg?.id)
        .filter(Boolean) as string[]) || [],
    [unreadThreads]
  );

  useEffect(() => {
    if (unreadThreads && unreadThreads.length > 0) {
      const threadNotifications = unreadThreads
        .filter((thread) => thread !== null && thread !== undefined)
        .map((thread, index) => {
          const publishedOfferId =
            thread?.offer?.offer_published?.offer_published_id;
          const policyNumber = thread?.offer?.offer_detail?.policy_number;

          if (!publishedOfferId) {
            return null;
          }

          return {
            id: `thread-${index}-${thread?.offer?.offer_id || index}`,
            title: `New thread message from ${thread?.sender_name}`,
            message: `You have received a new thread message on the ${thread.offer?.classofbusiness?.business_name} offer with policy number ${policyNumber} and insured ${thread.offer?.offer_detail?.insured_by}. Tap to view.`,
            time: new Date(thread?.created_at!).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            read: false,
            routePath: `/app/business/${publishedOfferId}`,
            offer: thread.offer,
            offerPublished: thread?.offer?.offer_published,
            participationId: thread?.offer_published_participant_id || "",
            policyNumber: policyNumber!,
          };
        })
        .filter(
          (notification): notification is NonNullable<typeof notification> =>
            notification !== null
        )
        .reverse();

      setNotifications(threadNotifications);
    } else {
      setNotifications([]);
    }
  }, [unreadThreads]);

  const handleThreadNotification = async (message: any) => {
    refresh();
    await notify("KAVA NOTIFICATION - NOTIFICATIONS", {
      body: message.data,
    });
  };

  const handleRealtimeEvent = async (eventName: string) => {
    refresh();
    await notify("KAVA NOTIFICATION - READ THREAD", { body: eventName });
  };
  const toggleNotifications = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (notification: Notification) => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((notif) =>
        notif.id === notification.id ? { ...notif, read: true } : notif
      )
    );

    setIsOpen(false);

    const searchParams = new URLSearchParams();
    if (notification.policyNumber) {
      searchParams.set("search", notification.policyNumber);
    }

    window.location.href = `/app/businesses?${searchParams.toString()}`;
  };

  const unreadCount = notifications.length;

  useRealtimeConnection({
    subscriptions: [
      {
        channel: config.realtime.channels.KAVA_NOTIFICATION,
        events: [
          config.realtime.events.BROKER_READ_THREAD,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
          config.realtime.events.REINSURER_READ_THREAD,
        ],
      },
    ],
    callback: handleRealtimeEvent,
  });

  return (
    <>
      {(unreadCount > 0 || isOpen) && (
        <div className="fixed bottom-4 right-4 z-50">
          <button
            onClick={toggleNotifications}
            className="relative p-3 bg-orange-500 rounded-full shadow-lg hover:bg-orange-600 focus:outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1"
              />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {isOpen && (
            <div className="absolute bottom-16 right-0 w-80 bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <h3 className="text-sm font-semibold text-gray-700">
                  Notifications
                </h3>
                <span className="text-xs text-gray-500">
                  {notifications.length} unread
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    Loading notifications...
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No unread notifications
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {notifications.map((notification) => (
                      <li
                        key={notification.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer bg-blue-50"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {notification.message}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {notification.time}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {showThreadContainer && activeThread && (
        <OfferThreadContainer
          open={showThreadContainer}
          setOpen={setShowThreadContainer}
          offer={activeThread.offer}
          offerPublished={activeThread.offerPublished}
          participationId={activeThread.participationId}
          unreadMessageIds={unreadMessageIds}
          refetch={refresh}
        />
      )}
    </>
  );
};

export default NotificationBell;
